import React from 'react';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useModules } from '../../context/ModuleContext';
import { Card } from '../../design-system/Card';
import { Button } from '../../design-system/Button';
import { Badge } from '../../design-system/Badge';
import {
  Plus,
  Compass,
  ArrowRight,
  TrendingUp,
  CheckCircle2,
  AlertCircle,
  Activity,
  Layers
} from 'lucide-react';

export const WorkspaceHome = ({ setCurrentView, setCurrentProjectView }) => {
  const {
    activeWorkspace,
    projects,
    setActiveProjectId,
    allTasks,
    setIsQuickAddOpen
  } = useWorkspace();
  const { enabledModules } = useModules();

  const totalTasks = allTasks.length;
  const completedTasks = allTasks.filter((t) => t.status === 'done').length;
  const inProgressTasks = allTasks.filter((t) => t.status === 'in_progress').length;
  const overdueTasks = allTasks.filter(
    (t) => t.status !== 'done' && t.dueDate && new Date(t.dueDate) < new Date()
  ).length;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }} className="animate-fade-in">
      {/* Workspace Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '16px'
        }}
      >
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <h1 style={{ fontSize: '26px', fontWeight: '800' }}>{activeWorkspace?.name}</h1>
            <Badge variant="teal" size="xs">
              {enabledModules.length} Modules Active
            </Badge>
          </div>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px' }}>
            Modular workspace overview • {projects.length} active initiatives
          </p>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          <Button
            variant="recessed"
            size="md"
            icon={<Compass size={16} />}
            onClick={() => setCurrentView('module_library')}
          >
            Module Library
          </Button>
          <Button
            variant="primary"
            size="md"
            icon={<Plus size={16} />}
            onClick={() => setIsQuickAddOpen(true)}
          >
            New Task
          </Button>
        </div>
      </div>

      {/* Metrics Row */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
          gap: '16px'
        }}
      >
        <Card padding="18px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
              TOTAL TASKS
            </span>
            <Layers size={18} color="var(--accent-teal)" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: '800', margin: '8px 0 4px' }}>
            {totalTasks}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Across all enabled projects
          </div>
        </Card>

        <Card padding="18px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
              IN PROGRESS
            </span>
            <TrendingUp size={18} color="var(--accent-teal)" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: '800', margin: '8px 0 4px', color: 'var(--accent-teal)' }}>
            {inProgressTasks}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Active velocity
          </div>
        </Card>

        <Card padding="18px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
              COMPLETED
            </span>
            <CheckCircle2 size={18} color="var(--color-success)" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: '800', margin: '8px 0 4px', color: 'var(--color-success)' }}>
            {completedTasks}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            {totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0}% completion rate
          </div>
        </Card>

        <Card padding="18px">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-muted)' }}>
              ATTENTION / OVERDUE
            </span>
            <AlertCircle size={18} color="var(--color-danger)" />
          </div>
          <div style={{ fontSize: '28px', fontWeight: '800', margin: '8px 0 4px', color: overdueTasks > 0 ? 'var(--color-danger)' : 'var(--text-muted)' }}>
            {overdueTasks}
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Require attention
          </div>
        </Card>
      </div>

      {/* Projects Grid */}
      <div>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '16px'
          }}
        >
          <h2 style={{ fontSize: '19px', fontWeight: '700' }}>Active Projects</h2>
          <span style={{ fontSize: '13px', color: 'var(--text-muted)' }}>
            Click a project to open its workspace
          </span>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '18px'
          }}
        >
          {projects.map((p) => {
            const projectTasks = allTasks.filter((t) => t.projectId === p.id);
            const projectCompleted = projectTasks.filter((t) => t.status === 'done').length;
            const progress = projectTasks.length > 0
              ? Math.round((projectCompleted / projectTasks.length) * 100)
              : p.progress;

            return (
              <Card
                key={p.id}
                interactive
                onClick={() => {
                  setActiveProjectId(p.id);
                  setCurrentView('project_view');
                  setCurrentProjectView('board');
                }}
                padding="22px"
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <Badge variant="teal" size="xs">
                    {p.key}
                  </Badge>
                  <Badge variant="recessed" size="xs">
                    {p.category}
                  </Badge>
                </div>

                <h3 style={{ fontSize: '17px', fontWeight: '700', marginBottom: '6px' }}>
                  {p.name}
                </h3>

                <p
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-secondary)',
                    lineHeight: '1.5',
                    marginBottom: '16px',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    overflow: 'hidden'
                  }}
                >
                  {p.description}
                </p>

                {/* Progress Bar */}
                <div style={{ marginBottom: '14px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', marginBottom: '6px' }}>
                    <span style={{ color: 'var(--text-muted)' }}>Progress</span>
                    <span style={{ fontWeight: '700', color: 'var(--accent-teal)' }}>{progress}%</span>
                  </div>
                  <div
                    style={{
                      height: '6px',
                      borderRadius: '999px',
                      backgroundColor: 'var(--surface-recessed)',
                      overflow: 'hidden'
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        width: `${progress}%`,
                        backgroundColor: 'var(--accent-teal)',
                        borderRadius: '999px',
                        transition: 'width 0.3s ease'
                      }}
                    />
                  </div>
                </div>

                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    paddingTop: '10px',
                    borderTop: '1px solid var(--border-divider)',
                    fontSize: '12px',
                    color: 'var(--text-muted)'
                  }}
                >
                  <span>Lead: {p.lead}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: 'var(--accent-teal)', fontWeight: '600' }}>
                    Open Board <ArrowRight size={14} />
                  </span>
                </div>
              </Card>
            );
          })}
        </div>
      </div>

      {/* Recent Activity Feed */}
      <div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
          <Activity size={18} color="var(--accent-teal)" />
          <h2 style={{ fontSize: '18px', fontWeight: '700' }}>Recent Workspace Activity</h2>
        </div>

        <Card padding="16px">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--accent-teal)' }}>
                  check_circle
                </span>
                <span>
                  <strong>Rohith Prince</strong> completed task <strong>FOR-104</strong> (Catalyst AppSail Node.js 22 Backend Skeleton)
                </span>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>10 mins ago</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--color-purple)' }}>
                  edit_note
                </span>
                <span>
                  <strong>Sarah Connor</strong> updated storyboards for <strong>District 4 Cyber-Alley</strong>
                </span>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>1 hour ago</span>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="material-symbols-outlined" style={{ fontSize: '18px', color: 'var(--color-warning)' }}>
                  memory
                </span>
                <span>
                  <strong>Marcus Vance</strong> uploaded BOM component quotes for <strong>Tactile Controller H1</strong>
                </span>
              </div>
              <span style={{ fontSize: '12px', color: 'var(--text-muted)' }}>3 hours ago</span>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};
